/**
 * Onde a cotação parou, na linguagem do comprador. A etapa vem calculada pelo
 * servidor (SituacaoCotacao.php); aqui fica só como ela aparece e para onde o
 * botão "próximo passo" leva.
 */
export type Etapa = 'aguardando_propostas' | 'conferir_equalizacao' | 'negociar' | 'aguardando_revisao' | 'fechada';

export type SituacaoFornecedor = 'respondeu' | 'aguardando' | 'sem_proposta';

export type Situacao = {
  etapa: Etapa;
  rodada: { numero: number; nome: string };
  respostas: number;
  equalizacao: 'nao_vista' | 'desatualizada' | 'em_dia';
  fornecedores: { apelido: string; situacao: SituacaoFornecedor }[];
  ultima_acao: { texto: string; em: string | null };
};

/** O que a listagem e o cabeçalho das telas mostram do pacote fechado. */
export type ResumoFechamento = { fornecedor: string | null; valor_total: number; ganho: number; ganho_pct: number };

/** Os passos de uma cotação, do ERP ao pedido. */
export const PASSOS = ['Planilha', 'Propostas', 'Equalização', 'Negociação', 'Fechamento'] as const;

type Tom = 'espera' | 'acao' | 'pronto';

export const ETAPAS: Record<Etapa, { rotulo: string; passo: number; tom: Tom; botao: string; destino: string }> = {
  aguardando_propostas: {
    rotulo: 'Aguardando propostas',
    passo: 1,
    tom: 'espera',
    botao: 'Carregar propostas',
    destino: 'propostas',
  },
  conferir_equalizacao: {
    rotulo: 'Conferir equalização',
    passo: 2,
    tom: 'acao',
    botao: 'Ver equalização',
    destino: 'equalizacao',
  },
  negociar: {
    rotulo: 'Pronta para negociar',
    passo: 3,
    tom: 'pronto',
    botao: 'Analisar e negociar',
    destino: 'analise',
  },
  aguardando_revisao: {
    rotulo: 'Aguardando nova proposta',
    passo: 3,
    tom: 'espera',
    botao: 'Carregar resposta',
    destino: 'propostas',
  },
  fechada: {
    rotulo: 'Fechada',
    passo: 4,
    tom: 'pronto',
    botao: 'Ver fechamento',
    destino: 'fechamento',
  },
};

/** Ordem dos filtros: primeiro o que depende do comprador. */
export const ORDEM_ETAPAS: Etapa[] = ['conferir_equalizacao', 'negociar', 'aguardando_revisao', 'aguardando_propostas', 'fechada'];

export const TOM_SELO: Record<Tom, string> = {
  espera: 'bg-warning/10 text-warning',
  acao: 'bg-accent/10 text-accent',
  pronto: 'bg-success/10 text-success',
};

export const TOM_PONTO: Record<Tom, string> = {
  espera: 'bg-warning',
  acao: 'bg-accent',
  pronto: 'bg-success',
};

function lista(nomes: string[]): string {
  if (nomes.length <= 1) return nomes.join('');
  return `${nomes.slice(0, -1).join(', ')} e ${nomes[nomes.length - 1]}`;
}

/** A frase de "o que falta", montada com quem já respondeu e quem ainda deve. */
export function oQueFalta(situacao: Situacao): string {
  const semProposta = situacao.fornecedores.filter((f) => f.situacao === 'sem_proposta').map((f) => f.apelido);
  const aguardando = situacao.fornecedores.filter((f) => f.situacao === 'aguardando').map((f) => f.apelido);

  switch (situacao.etapa) {
    case 'aguardando_propostas':
      return situacao.rodada.numero === 1 && semProposta.length
        ? `Faltam as propostas de ${lista(semProposta)}.`
        : 'Nenhuma proposta nova nesta rodada ainda.';
    case 'conferir_equalizacao':
      return situacao.equalizacao === 'desatualizada'
        ? 'Chegou proposta depois da última conferência: veja a equalização de novo.'
        : `${situacao.respostas} de ${situacao.fornecedores.length} propostas carregadas: veja a equalização para saber o alvo.`;
    case 'negociar':
      return 'Equalização em dia: analise e peça nova proposta a quem está acima do alvo.';
    case 'aguardando_revisao':
      return `Esperando a resposta de ${lista(aguardando)}.`;
    case 'fechada':
      return 'Pacote fechado: baixe a planilha do pedido para lançar no Compumate.';
  }
}

const hora = new Intl.DateTimeFormat('pt-BR', { hour: '2-digit', minute: '2-digit' });
const diaMes = new Intl.DateTimeFormat('pt-BR', { day: '2-digit', month: '2-digit' });
const completo = new Intl.DateTimeFormat('pt-BR', { dateStyle: 'short', timeStyle: 'short' });

function mesmoDia(a: Date, b: Date): boolean {
  return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
}

/** "há 12 min", "hoje às 14:32", "ontem às 09:10", "12/09 às 16:03". */
export function quandoFoi(iso: string | null, agora = new Date()): string {
  if (!iso) return '';

  const data = new Date(iso);
  const minutos = Math.floor((agora.getTime() - data.getTime()) / 60000);

  if (minutos < 1) return 'agora há pouco';
  if (minutos < 60) return `há ${minutos} min`;
  if (mesmoDia(data, agora)) return `hoje às ${hora.format(data)}`;

  const ontem = new Date(agora);
  ontem.setDate(agora.getDate() - 1);
  if (mesmoDia(data, ontem)) return `ontem às ${hora.format(data)}`;

  return `${diaMes.format(data)} às ${hora.format(data)}`;
}

export function dataCompleta(iso: string | null): string {
  return iso ? completo.format(new Date(iso)) : '';
}
